class PtpContactForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.select = this.querySelector('[data-company-select]');
    this.field = this.querySelector('[data-company-field]');
    this.input = this.field ? this.field.querySelector('[data-company-input]') : null;

    this.phoneGroup = this.querySelector('[data-phone-group]');
    this.phoneInput = this.querySelector('[data-phone-input]');
    this.countrySelect = null;

    // Rendered by Liquid only after a successful post — the reliable signal that
    // this is a fresh form rather than an error re-render.
    this.success = this.querySelector('[data-contact-success]');

    this.triggerValue = (this.dataset.triggerValue || '').trim().toLowerCase();
    this.storageKey = 'ptp-contact-form:' + (this.id || 'default');

    // Injected here rather than rendered in Liquid: without JS the phone field
    // stays a plain tel input and the form still submits correctly.
    this.buildCountrySelect();

    this.focusSuccess();
    this.restore();
    this.applyUrlSubject();

    if (this.select) {
      this.onChange = this.handleChange.bind(this);
      this.select.addEventListener('change', this.onChange);
    }

    if (this.form) {
      this.onSubmit = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
    }

    this.sync();
  }

  disconnectedCallback() {
    if (this.select && this.onChange) {
      this.select.removeEventListener('change', this.onChange);
    }
    if (this.form && this.onSubmit) {
      this.form.removeEventListener('submit', this.onSubmit);
    }
  }

  buildCountrySelect() {
    if (!this.phoneGroup || !this.phoneInput) return;
    if (!Array.isArray(PtpContactForm.COUNTRIES) || !PtpContactForm.COUNTRIES.length) return;

    const select = document.createElement('select');
    select.className = 'eg-contact-select eg-phone__country';
    select.setAttribute('data-phone-country', '');
    select.setAttribute('aria-label', this.dataset.phoneCountryLabel || 'Country calling code');

    // Priority markets first, then a separator, then everyone alphabetically —
    // the array is already in that order, so a single pass preserves it.
    for (const country of PtpContactForm.COUNTRIES) {
      if (country.separator) {
        const divider = document.createElement('option');
        divider.disabled = true;
        divider.textContent = '──────────';
        select.appendChild(divider);
        continue;
      }

      const option = document.createElement('option');
      option.value = country.iso;
      option.dataset.dial = country.dial;
      option.textContent = country.iso + ' (+' + country.dial + ')';
      select.appendChild(option);
    }

    const fallback = (this.dataset.defaultCountry || 'US').trim().toUpperCase();
    if (this.hasCountry(select, fallback)) {
      select.value = fallback;
    } else if (this.hasCountry(select, 'US')) {
      select.value = 'US';
    }

    this.countrySelect = select;
    this.phoneGroup.insertBefore(select, this.phoneInput);
  }

  hasCountry(select, iso) {
    return Array.prototype.some.call(select.options, (option) => option.value === iso);
  }

  handleChange() {
    this.sync();
  }

  // Shopify's contact `form` object exposes only name/email/phone/body, so
  // Subject, Company and the chosen country cannot be repopulated from Liquid
  // after a failed submit. Stash them so the error re-render can put them back.
  handleSubmit() {
    this.combinePhone();

    if (!this.select && !this.countrySelect) return;

    this.write({
      subject: this.select ? this.select.value : '',
      company: this.input ? this.input.value : '',
      // The raw local number is stashed so restore() can undo the combine below
      // instead of re-prefixing an already-prefixed value on the error re-render.
      phoneCountry: this.countrySelect ? this.countrySelect.value : '',
      phoneNumber: this.phoneRaw != null ? this.phoneRaw : '',
    });
  }

  // Fold the dial code into contact[phone] so the notification email carries one
  // complete number. Skipped when empty (no lonely dial code) or when the user
  // already typed a leading "+" (assume they entered a full international number).
  combinePhone() {
    if (!this.countrySelect || !this.phoneInput) return;

    const number = this.phoneInput.value.trim();
    this.phoneRaw = number;

    if (!number || number.charAt(0) === '+') return;

    const option = this.countrySelect.options[this.countrySelect.selectedIndex];
    const dial = option ? option.dataset.dial : '';
    if (!dial) return;

    this.phoneInput.value = '+' + dial + ' ' + number;
  }

  restore() {
    const saved = this.read();
    this.clear();

    if (!saved || this.success) return;
    if (this.select && saved.subject) this.select.value = saved.subject;
    if (this.input && saved.company) this.input.value = saved.company;

    if (this.countrySelect && saved.phoneCountry && this.hasCountry(this.countrySelect, saved.phoneCountry)) {
      this.countrySelect.value = saved.phoneCountry;
    }
    // The re-rendered input holds the combined "+dial number"; swap it back to
    // the raw number so a resubmit does not stack a second dial code on top.
    if (this.phoneInput && saved.phoneNumber != null) {
      this.phoneInput.value = saved.phoneNumber;
    }
  }

  focusSuccess() {
    if (this.success) this.success.focus();
  }

  applyUrlSubject() {
    if (!this.select || this.select.value || this.success) return;

    const requested = (new URLSearchParams(window.location.search).get('subject') || '').trim().toLowerCase();
    if (!requested) return;

    const match = Array.prototype.find.call(
      this.select.options,
      (option) => option.value && option.value.trim().toLowerCase() === requested
    );

    if (match) this.select.value = match.value;
  }

  sync() {
    if (!this.select || !this.field) return;

    const show = this.select.value.trim().toLowerCase() === this.triggerValue;
    this.field.classList.toggle('is-hidden', !show);

    if (!this.input) return;

    // Required only while visible. A hidden `required` control blocks submit
    // with an element the user can neither see nor focus.
    this.input.required = show;
    this.input.setAttribute('aria-required', show ? 'true' : 'false');

    // Disabled controls are not submitted, so a hidden Company value never
    // reaches the notification email, while the typed value survives a toggle.
    this.input.disabled = !show;
  }

  read() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  write(value) {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(value));
    } catch (error) {
      // Storage unavailable (private mode, quota) — persistence is optional.
    }
  }

  clear() {
    try {
      sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      // Storage unavailable — nothing to clean up.
    }
  }
}

// ISO 3166-1 alpha-2 code, display name, and E.164 country calling code.
// `separator: true` marks the visual divider after the priority markets.
// Dial codes are stable ITU assignments; spot-check less-common entries before
// relying on them for anything beyond display.
PtpContactForm.COUNTRIES = [
  { iso: 'US', name: 'United States', dial: '1' },
  { iso: 'CA', name: 'Canada', dial: '1' },
  { separator: true },
  { iso: 'AF', name: 'Afghanistan', dial: '93' },
  { iso: 'AL', name: 'Albania', dial: '355' },
  { iso: 'DZ', name: 'Algeria', dial: '213' },
  { iso: 'AD', name: 'Andorra', dial: '376' },
  { iso: 'AO', name: 'Angola', dial: '244' },
  { iso: 'AG', name: 'Antigua and Barbuda', dial: '1' },
  { iso: 'AR', name: 'Argentina', dial: '54' },
  { iso: 'AM', name: 'Armenia', dial: '374' },
  { iso: 'AU', name: 'Australia', dial: '61' },
  { iso: 'AT', name: 'Austria', dial: '43' },
  { iso: 'AZ', name: 'Azerbaijan', dial: '994' },
  { iso: 'BS', name: 'Bahamas', dial: '1' },
  { iso: 'BH', name: 'Bahrain', dial: '973' },
  { iso: 'BD', name: 'Bangladesh', dial: '880' },
  { iso: 'BB', name: 'Barbados', dial: '1' },
  { iso: 'BY', name: 'Belarus', dial: '375' },
  { iso: 'BE', name: 'Belgium', dial: '32' },
  { iso: 'BZ', name: 'Belize', dial: '501' },
  { iso: 'BJ', name: 'Benin', dial: '229' },
  { iso: 'BT', name: 'Bhutan', dial: '975' },
  { iso: 'BO', name: 'Bolivia', dial: '591' },
  { iso: 'BA', name: 'Bosnia and Herzegovina', dial: '387' },
  { iso: 'BW', name: 'Botswana', dial: '267' },
  { iso: 'BR', name: 'Brazil', dial: '55' },
  { iso: 'BN', name: 'Brunei', dial: '673' },
  { iso: 'BG', name: 'Bulgaria', dial: '359' },
  { iso: 'BF', name: 'Burkina Faso', dial: '226' },
  { iso: 'BI', name: 'Burundi', dial: '257' },
  { iso: 'KH', name: 'Cambodia', dial: '855' },
  { iso: 'CM', name: 'Cameroon', dial: '237' },
  { iso: 'CV', name: 'Cape Verde', dial: '238' },
  { iso: 'CF', name: 'Central African Republic', dial: '236' },
  { iso: 'TD', name: 'Chad', dial: '235' },
  { iso: 'CL', name: 'Chile', dial: '56' },
  { iso: 'CN', name: 'China', dial: '86' },
  { iso: 'CO', name: 'Colombia', dial: '57' },
  { iso: 'KM', name: 'Comoros', dial: '269' },
  { iso: 'CG', name: 'Congo (Republic)', dial: '242' },
  { iso: 'CD', name: 'Congo (DRC)', dial: '243' },
  { iso: 'CR', name: 'Costa Rica', dial: '506' },
  { iso: 'HR', name: 'Croatia', dial: '385' },
  { iso: 'CU', name: 'Cuba', dial: '53' },
  { iso: 'CY', name: 'Cyprus', dial: '357' },
  { iso: 'CZ', name: 'Czechia', dial: '420' },
  { iso: 'DK', name: 'Denmark', dial: '45' },
  { iso: 'DJ', name: 'Djibouti', dial: '253' },
  { iso: 'DM', name: 'Dominica', dial: '1' },
  { iso: 'DO', name: 'Dominican Republic', dial: '1' },
  { iso: 'EC', name: 'Ecuador', dial: '593' },
  { iso: 'EG', name: 'Egypt', dial: '20' },
  { iso: 'SV', name: 'El Salvador', dial: '503' },
  { iso: 'GQ', name: 'Equatorial Guinea', dial: '240' },
  { iso: 'ER', name: 'Eritrea', dial: '291' },
  { iso: 'EE', name: 'Estonia', dial: '372' },
  { iso: 'SZ', name: 'Eswatini', dial: '268' },
  { iso: 'ET', name: 'Ethiopia', dial: '251' },
  { iso: 'FJ', name: 'Fiji', dial: '679' },
  { iso: 'FI', name: 'Finland', dial: '358' },
  { iso: 'FR', name: 'France', dial: '33' },
  { iso: 'GA', name: 'Gabon', dial: '241' },
  { iso: 'GM', name: 'Gambia', dial: '220' },
  { iso: 'GE', name: 'Georgia', dial: '995' },
  { iso: 'DE', name: 'Germany', dial: '49' },
  { iso: 'GH', name: 'Ghana', dial: '233' },
  { iso: 'GR', name: 'Greece', dial: '30' },
  { iso: 'GD', name: 'Grenada', dial: '1' },
  { iso: 'GT', name: 'Guatemala', dial: '502' },
  { iso: 'GN', name: 'Guinea', dial: '224' },
  { iso: 'GW', name: 'Guinea-Bissau', dial: '245' },
  { iso: 'GY', name: 'Guyana', dial: '592' },
  { iso: 'HT', name: 'Haiti', dial: '509' },
  { iso: 'HN', name: 'Honduras', dial: '504' },
  { iso: 'HK', name: 'Hong Kong', dial: '852' },
  { iso: 'HU', name: 'Hungary', dial: '36' },
  { iso: 'IS', name: 'Iceland', dial: '354' },
  { iso: 'IN', name: 'India', dial: '91' },
  { iso: 'ID', name: 'Indonesia', dial: '62' },
  { iso: 'IR', name: 'Iran', dial: '98' },
  { iso: 'IQ', name: 'Iraq', dial: '964' },
  { iso: 'IE', name: 'Ireland', dial: '353' },
  { iso: 'IL', name: 'Israel', dial: '972' },
  { iso: 'IT', name: 'Italy', dial: '39' },
  { iso: 'CI', name: 'Ivory Coast', dial: '225' },
  { iso: 'JM', name: 'Jamaica', dial: '1' },
  { iso: 'JP', name: 'Japan', dial: '81' },
  { iso: 'JO', name: 'Jordan', dial: '962' },
  { iso: 'KZ', name: 'Kazakhstan', dial: '7' },
  { iso: 'KE', name: 'Kenya', dial: '254' },
  { iso: 'KI', name: 'Kiribati', dial: '686' },
  { iso: 'XK', name: 'Kosovo', dial: '383' },
  { iso: 'KW', name: 'Kuwait', dial: '965' },
  { iso: 'KG', name: 'Kyrgyzstan', dial: '996' },
  { iso: 'LA', name: 'Laos', dial: '856' },
  { iso: 'LV', name: 'Latvia', dial: '371' },
  { iso: 'LB', name: 'Lebanon', dial: '961' },
  { iso: 'LS', name: 'Lesotho', dial: '266' },
  { iso: 'LR', name: 'Liberia', dial: '231' },
  { iso: 'LY', name: 'Libya', dial: '218' },
  { iso: 'LI', name: 'Liechtenstein', dial: '423' },
  { iso: 'LT', name: 'Lithuania', dial: '370' },
  { iso: 'LU', name: 'Luxembourg', dial: '352' },
  { iso: 'MO', name: 'Macau', dial: '853' },
  { iso: 'MG', name: 'Madagascar', dial: '261' },
  { iso: 'MW', name: 'Malawi', dial: '265' },
  { iso: 'MY', name: 'Malaysia', dial: '60' },
  { iso: 'MV', name: 'Maldives', dial: '960' },
  { iso: 'ML', name: 'Mali', dial: '223' },
  { iso: 'MT', name: 'Malta', dial: '356' },
  { iso: 'MH', name: 'Marshall Islands', dial: '692' },
  { iso: 'MR', name: 'Mauritania', dial: '222' },
  { iso: 'MU', name: 'Mauritius', dial: '230' },
  { iso: 'MX', name: 'Mexico', dial: '52' },
  { iso: 'FM', name: 'Micronesia', dial: '691' },
  { iso: 'MD', name: 'Moldova', dial: '373' },
  { iso: 'MC', name: 'Monaco', dial: '377' },
  { iso: 'MN', name: 'Mongolia', dial: '976' },
  { iso: 'ME', name: 'Montenegro', dial: '382' },
  { iso: 'MA', name: 'Morocco', dial: '212' },
  { iso: 'MZ', name: 'Mozambique', dial: '258' },
  { iso: 'MM', name: 'Myanmar', dial: '95' },
  { iso: 'NA', name: 'Namibia', dial: '264' },
  { iso: 'NR', name: 'Nauru', dial: '674' },
  { iso: 'NP', name: 'Nepal', dial: '977' },
  { iso: 'NL', name: 'Netherlands', dial: '31' },
  { iso: 'NZ', name: 'New Zealand', dial: '64' },
  { iso: 'NI', name: 'Nicaragua', dial: '505' },
  { iso: 'NE', name: 'Niger', dial: '227' },
  { iso: 'NG', name: 'Nigeria', dial: '234' },
  { iso: 'KP', name: 'North Korea', dial: '850' },
  { iso: 'MK', name: 'North Macedonia', dial: '389' },
  { iso: 'NO', name: 'Norway', dial: '47' },
  { iso: 'OM', name: 'Oman', dial: '968' },
  { iso: 'PK', name: 'Pakistan', dial: '92' },
  { iso: 'PW', name: 'Palau', dial: '680' },
  { iso: 'PS', name: 'Palestine', dial: '970' },
  { iso: 'PA', name: 'Panama', dial: '507' },
  { iso: 'PG', name: 'Papua New Guinea', dial: '675' },
  { iso: 'PY', name: 'Paraguay', dial: '595' },
  { iso: 'PE', name: 'Peru', dial: '51' },
  { iso: 'PH', name: 'Philippines', dial: '63' },
  { iso: 'PL', name: 'Poland', dial: '48' },
  { iso: 'PT', name: 'Portugal', dial: '351' },
  { iso: 'QA', name: 'Qatar', dial: '974' },
  { iso: 'RO', name: 'Romania', dial: '40' },
  { iso: 'RU', name: 'Russia', dial: '7' },
  { iso: 'RW', name: 'Rwanda', dial: '250' },
  { iso: 'KN', name: 'Saint Kitts and Nevis', dial: '1' },
  { iso: 'LC', name: 'Saint Lucia', dial: '1' },
  { iso: 'VC', name: 'Saint Vincent and the Grenadines', dial: '1' },
  { iso: 'WS', name: 'Samoa', dial: '685' },
  { iso: 'SM', name: 'San Marino', dial: '378' },
  { iso: 'ST', name: 'Sao Tome and Principe', dial: '239' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '966' },
  { iso: 'SN', name: 'Senegal', dial: '221' },
  { iso: 'RS', name: 'Serbia', dial: '381' },
  { iso: 'SC', name: 'Seychelles', dial: '248' },
  { iso: 'SL', name: 'Sierra Leone', dial: '232' },
  { iso: 'SG', name: 'Singapore', dial: '65' },
  { iso: 'SK', name: 'Slovakia', dial: '421' },
  { iso: 'SI', name: 'Slovenia', dial: '386' },
  { iso: 'SB', name: 'Solomon Islands', dial: '677' },
  { iso: 'SO', name: 'Somalia', dial: '252' },
  { iso: 'ZA', name: 'South Africa', dial: '27' },
  { iso: 'KR', name: 'South Korea', dial: '82' },
  { iso: 'SS', name: 'South Sudan', dial: '211' },
  { iso: 'ES', name: 'Spain', dial: '34' },
  { iso: 'LK', name: 'Sri Lanka', dial: '94' },
  { iso: 'SD', name: 'Sudan', dial: '249' },
  { iso: 'SR', name: 'Suriname', dial: '597' },
  { iso: 'SE', name: 'Sweden', dial: '46' },
  { iso: 'CH', name: 'Switzerland', dial: '41' },
  { iso: 'SY', name: 'Syria', dial: '963' },
  { iso: 'TW', name: 'Taiwan', dial: '886' },
  { iso: 'TJ', name: 'Tajikistan', dial: '992' },
  { iso: 'TZ', name: 'Tanzania', dial: '255' },
  { iso: 'TH', name: 'Thailand', dial: '66' },
  { iso: 'TL', name: 'Timor-Leste', dial: '670' },
  { iso: 'TG', name: 'Togo', dial: '228' },
  { iso: 'TO', name: 'Tonga', dial: '676' },
  { iso: 'TT', name: 'Trinidad and Tobago', dial: '1' },
  { iso: 'TN', name: 'Tunisia', dial: '216' },
  { iso: 'TR', name: 'Turkey', dial: '90' },
  { iso: 'TM', name: 'Turkmenistan', dial: '993' },
  { iso: 'TV', name: 'Tuvalu', dial: '688' },
  { iso: 'UG', name: 'Uganda', dial: '256' },
  { iso: 'UA', name: 'Ukraine', dial: '380' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971' },
  { iso: 'GB', name: 'United Kingdom', dial: '44' },
  { iso: 'UY', name: 'Uruguay', dial: '598' },
  { iso: 'UZ', name: 'Uzbekistan', dial: '998' },
  { iso: 'VU', name: 'Vanuatu', dial: '678' },
  { iso: 'VA', name: 'Vatican City', dial: '379' },
  { iso: 'VE', name: 'Venezuela', dial: '58' },
  { iso: 'VN', name: 'Vietnam', dial: '84' },
  { iso: 'YE', name: 'Yemen', dial: '967' },
  { iso: 'ZM', name: 'Zambia', dial: '260' },
  { iso: 'ZW', name: 'Zimbabwe', dial: '263' },
];

if (!customElements.get('ptp-contact-form')) {
  customElements.define('ptp-contact-form', PtpContactForm);
}
